import { useEffect, useMemo, useState } from "react";
import { GripVertical, Loader2, Plus, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * ChecklistEditorModal — create/edit a captain's own checklist template.
 *
 * Two entry points:
 *   1. New blank checklist (mode="new", template=null)
 *   2. Edit an existing user template (mode="edit", template=<obj>)
 *
 * All fields save via POST/PATCH `/api/checklists`. The endpoint is Pro-only,
 * so this modal is opened from a Pro-only surface (see ChecklistsView).
 *
 * Scope toggle covers option (g) — captain can pick whether the template
 * applies only to the current vessel or to all their boats (vessel_id=null).
 */

const KINDS = [
    { id: "custom", label: "Custom" },
    { id: "winterize", label: "Winterizing" },
    { id: "dewinterize", label: "Spring commissioning" },
    { id: "startup", label: "Pre-departure" },
];

function mkTempId() {
    return `new_${Math.random().toString(36).slice(2, 8)}`;
}

export default function ChecklistEditorModal({
    open,
    onClose,
    onSaved,
    vesselId,
    vesselName,
    template,      // null for new, obj for edit
    initialItems,  // for "duplicate from template" — pre-fills items
    initialName,   // for "duplicate from template" — pre-fills name
}) {
    const isEdit = !!template;
    const [name, setName] = useState("");
    const [kind, setKind] = useState("custom");
    const [description, setDescription] = useState("");
    const [items, setItems] = useState([]);
    const [scopeToVessel, setScopeToVessel] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!open) return;
        if (template) {
            setName(template.name || "");
            setKind(template.kind || "custom");
            setDescription(template.description || "");
            setItems((template.items || []).map((it) => ({ ...it, __key: it.id })));
            setScopeToVessel(template.vessel_id != null);
        } else {
            setName(initialName || "");
            setKind("custom");
            setDescription("");
            setItems(
                (initialItems || []).map((it) => ({
                    id: mkTempId(),
                    label: it.label || "",
                    note: it.note || null,
                    __key: mkTempId(),
                })),
            );
            setScopeToVessel(true);
        }
        setErr("");
    }, [open, template, initialItems, initialName]);

    const addItem = () =>
        setItems((cur) => [...cur, { id: mkTempId(), label: "", note: null, __key: mkTempId() }]);

    const updateItem = (idx, patch) =>
        setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

    const removeItem = (idx) =>
        setItems((cur) => cur.filter((_, i) => i !== idx));

    const moveItem = (idx, direction) => {
        setItems((cur) => {
            const next = [...cur];
            const target = idx + direction;
            if (target < 0 || target >= next.length) return cur;
            [next[idx], next[target]] = [next[target], next[idx]];
            return next;
        });
    };

    const canSave = useMemo(
        () => name.trim().length > 0 && items.some((it) => it.label.trim().length > 0),
        [name, items],
    );

    const save = async () => {
        if (!canSave || busy) return;
        setBusy(true);
        setErr("");
        try {
            const cleanItems = items
                .filter((it) => it.label.trim())
                .map((it, idx) => ({
                    // Preserve existing ids to keep run history stable; new
                    // items get a blank id and the backend fills it in.
                    id: String(it.id || "").startsWith("new_") ? "" : it.id,
                    label: it.label.trim(),
                    note: it.note ? String(it.note).trim() || null : null,
                    order: idx,
                }));
            const payload = {
                name: name.trim(),
                kind,
                description: description.trim() || null,
                items: cleanItems,
                vessel_id: scopeToVessel ? vesselId : null,
            };
            const url = isEdit ? `/checklists/${template.id}` : "/checklists";
            const method = isEdit ? "patch" : "post";
            const { data } = await api[method](url, payload);
            onSaved?.(data);
            onClose?.();
        } catch (e) {
            setErr(formatApiErrorDetail(e) || "Could not save.");
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="checklist-editor-modal"
            role="dialog"
            aria-label="Edit checklist"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            style={{
                position: "fixed", inset: 0, zIndex: 100,
                background: "rgba(4, 12, 22, 0.72)",
                display: "flex", alignItems: "flex-start", justifyContent: "center",
                padding: "40px 16px", overflowY: "auto",
            }}
        >
            <div className="bm-card" style={{ maxWidth: 620, width: "100%", padding: "22px 22px 26px", position: "relative" }}>
                <button
                    data-testid="checklist-editor-close-btn"
                    onClick={onClose}
                    aria-label="Close"
                    style={{
                        position: "absolute", top: 14, right: 14,
                        background: "none", border: "none", cursor: "pointer",
                        color: "var(--bm-text-muted)", padding: 6, display: "flex",
                    }}
                >
                    <X size={18} />
                </button>
                <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 6 }}>
                    {isEdit ? "Edit checklist" : "New checklist"}
                </div>
                <h2 className="bm-serif" style={{ margin: 0, fontSize: 24, fontWeight: 500 }}>
                    {name.trim() || (isEdit ? "Untitled checklist" : "Build your own list")}
                </h2>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 13, margin: "6px 0 18px" }}>
                    Steps you tick off every haul-out, launch, or before pulling lines. Runs snapshot the list, so edits here never rewrite history.
                </p>

                {/* Name */}
                <label style={{ display: "block", marginBottom: 12 }}>
                    <span style={{ display: "block", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-dim)", marginBottom: 5 }}>Title</span>
                    <input
                        data-testid="checklist-editor-name-input"
                        className="bm-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={120}
                        placeholder="e.g. Fall winterization"
                        style={{ width: "100%" }}
                    />
                </label>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <label>
                        <span style={{ display: "block", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-dim)", marginBottom: 5 }}>Kind</span>
                        <select
                            data-testid="checklist-editor-kind-select"
                            className="bm-input"
                            value={kind}
                            onChange={(e) => setKind(e.target.value)}
                            style={{ width: "100%" }}
                        >
                            {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                        </select>
                    </label>
                    <label>
                        <span style={{ display: "block", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-dim)", marginBottom: 5 }}>Scope</span>
                        <select
                            data-testid="checklist-editor-scope-select"
                            className="bm-input"
                            value={scopeToVessel ? "vessel" : "all"}
                            onChange={(e) => setScopeToVessel(e.target.value === "vessel")}
                            style={{ width: "100%" }}
                        >
                            <option value="vessel">{`Only ${vesselName || "this vessel"}`}</option>
                            <option value="all">All my boats</option>
                        </select>
                    </label>
                </div>

                <label style={{ display: "block", marginBottom: 18 }}>
                    <span style={{ display: "block", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-dim)", marginBottom: 5 }}>Description (optional)</span>
                    <textarea
                        data-testid="checklist-editor-description-input"
                        className="bm-input"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        maxLength={500}
                        rows={2}
                        placeholder="A note for future-you or the crew"
                        style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }}
                    />
                </label>

                {/* Items */}
                <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>
                    Items · {items.length}
                </div>
                <div data-testid="checklist-editor-items" style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                    {items.length === 0 && (
                        <div style={{ padding: 14, textAlign: "center", color: "var(--bm-text-muted)", fontSize: 13, border: "1px dashed var(--bm-border-soft)", borderRadius: 10 }}>
                            No steps yet — add your first below.
                        </div>
                    )}
                    {items.map((it, idx) => (
                        <div
                            key={it.__key || it.id}
                            data-testid={`checklist-editor-item-${idx}`}
                            style={{
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "8px 10px",
                                background: "rgba(255,255,255,0.02)",
                                border: "1px solid var(--bm-border-soft)",
                                borderRadius: 10,
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => moveItem(idx, -1)}
                                aria-label="Move up"
                                disabled={idx === 0}
                                style={{ background: "none", border: "none", cursor: idx === 0 ? "not-allowed" : "pointer", color: "var(--bm-text-muted)", padding: 4, opacity: idx === 0 ? 0.35 : 1 }}
                            >
                                <GripVertical size={16} />
                            </button>
                            <input
                                data-testid={`checklist-editor-item-input-${idx}`}
                                className="bm-input"
                                value={it.label}
                                onChange={(e) => updateItem(idx, { label: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (idx === items.length - 1) addItem(); } }}
                                maxLength={200}
                                placeholder="Step description"
                                style={{ flex: 1 }}
                            />
                            <button
                                type="button"
                                data-testid={`checklist-editor-item-remove-${idx}`}
                                onClick={() => removeItem(idx)}
                                aria-label="Remove item"
                                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--bm-text-dim)", padding: 6, display: "flex" }}
                            >
                                <Trash2 size={15} />
                            </button>
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    data-testid="checklist-editor-add-item-btn"
                    onClick={addItem}
                    className="bm-btn bm-btn-ghost"
                    style={{ padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 20 }}
                >
                    <Plus size={14} /> Add step
                </button>

                {err && (
                    <div data-testid="checklist-editor-error" style={{ color: "var(--bm-red, #d97a7a)", fontSize: 13, marginBottom: 12 }}>
                        {err}
                    </div>
                )}

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    <button
                        type="button"
                        data-testid="checklist-editor-cancel-btn"
                        onClick={onClose}
                        className="bm-btn bm-btn-ghost"
                        style={{ padding: "10px 18px", fontSize: 13 }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="checklist-editor-save-btn"
                        onClick={save}
                        disabled={!canSave || busy}
                        className="bm-btn bm-btn-gold"
                        style={{ padding: "10px 22px", fontSize: 13, opacity: (!canSave || busy) ? 0.55 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                        {busy && <Loader2 size={13} className="bm-spin" />}
                        {isEdit ? "Save changes" : "Create checklist"}
                    </button>
                </div>
            </div>
        </div>
    );
}
